import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
	buildPlatformSessionByRecordId,
	buildProntuarioSortedSessionsList,
} from '../../utils/buildProntuarioSortedSessionsList';
import { formatProntuarioSessionDate } from '../../utils/formatProntuarioSessionDate';
import { resolveDefaultSessaoNumero } from '../../utils/resolveDefaultSessaoNumero';
import {
	PRONTUARIO_SESSIONS_SUBTITLE_CLASS,
	PRONTUARIO_SESSIONS_TITLE_CLASS,
} from '../../constants/prontuarioTheme';
import {
	ProntuarioSessionCard,
	ProntuarioSessionsListSkeleton,
} from './ProntuarioSessionCard';

type SessionCallProntuarioSessionsListViewProps = {
	currentSession?: Sessions.Session | null;
	recordSessions?: MedicalRecords.MedicalRecordSession[] | null;
	platformSessions?: Sessions.Session[] | null;
	isLoading?: boolean;
	hasMorePastSessions?: boolean;
	isLoadingMorePastSessions?: boolean;
	onLoadMorePastSessions?: () => void;
	onOpenCurrentSession?: () => void;
	onOpenPastSession?: (sessionId: string) => void;
	className?: string;
};

const SessionCallProntuarioSessionsListView = ({
	currentSession,
	recordSessions,
	platformSessions,
	isLoading = false,
	hasMorePastSessions = false,
	isLoadingMorePastSessions = false,
	onLoadMorePastSessions,
	onOpenCurrentSession,
	onOpenPastSession,
	className,
}: SessionCallProntuarioSessionsListViewProps) => {
	const pastSessions = useMemo(
		() =>
			buildProntuarioSortedSessionsList({
				recordSessions,
				platformSessions,
				currentMedicalRecordSessionId:
					currentSession?.medical_record_session?.id,
				currentShortSummary:
					currentSession?.medical_record_session?.short_summary,
				excludePlatformSessionId: currentSession?.id,
			}),
		[
			currentSession?.id,
			currentSession?.medical_record_session?.id,
			currentSession?.medical_record_session?.short_summary,
			platformSessions,
			recordSessions,
		],
	);

	const platformSessionByRecordId = useMemo(
		() =>
			buildPlatformSessionByRecordId(
				recordSessions ?? [],
				platformSessions,
			),
		[platformSessions, recordSessions],
	);

	const currentSessionNumber = resolveDefaultSessaoNumero(
		currentSession,
		recordSessions,
	);
	const currentDateLabel = formatProntuarioSessionDate(
		currentSession?.selected_date,
	);

	const loadMoreButtonClass =
		'mt-4 w-full rounded-lg border border-white/[0.08] py-2.5 text-[14px] font-medium text-[#8ab4f8] transition-colors hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-60';

	return (
		<div className={cn('min-w-0', className)}>
			<h2 className={PRONTUARIO_SESSIONS_TITLE_CLASS}>Sessões</h2>
			<p className={cn(PRONTUARIO_SESSIONS_SUBTITLE_CLASS, 'pb-4')}>
				Selecione uma sessão para revisar as anotações.
			</p>

			{isLoading ? (
				<ProntuarioSessionsListSkeleton variant="dark" />
			) : (
				<div className="min-w-0">
					<ul className="space-y-2.5">
						{currentSession ? (
							<li>
								<ProntuarioSessionCard
									variant="dark"
									isCurrent
									sessionNumber={currentSessionNumber}
									dateLabel={currentDateLabel}
									shortSummary={
										currentSession.medical_record_session
											?.short_summary
									}
									shortSummaryTag={
										currentSession.medical_record_session
											?.short_summary_tag
									}
									platformSession={currentSession}
									onClick={onOpenCurrentSession}
								/>
							</li>
						) : null}

						{pastSessions.map((record) => (
							<li key={record.id}>
								<ProntuarioSessionCard
									variant="dark"
									sessionNumber={record.sessaoNumero ?? '—'}
									dateLabel={record.data ?? '—'}
									shortSummary={record.resumoCurto}
									shortSummaryTag={
										record.resumoCurtoTag ??
										record.resumoTag
									}
									platformSession={
										platformSessionByRecordId.get(
											record.id,
										) ?? null
									}
									onClick={() =>
										onOpenPastSession?.(record.id)
									}
								/>
							</li>
						))}
					</ul>

					{hasMorePastSessions ? (
						<button
							type="button"
							onClick={onLoadMorePastSessions}
							disabled={isLoadingMorePastSessions}
							className={loadMoreButtonClass}
						>
							{isLoadingMorePastSessions
								? 'Carregando...'
								: 'Mostrar mais'}
						</button>
					) : null}
				</div>
			)}
		</div>
	);
};

export { SessionCallProntuarioSessionsListView };
